import React, { useCallback, useEffect, useMemo } from 'react';

import delv from 'dlv';
import { Helmet } from 'react-helmet-async';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import LoadingAnimation from 'components/loading-animation';
import LoadingContainer from 'components/loading-animation/loading-container';
import { BackArrowHistory } from 'components/navigation/back-arrow';
import useEffectOnce from 'hooks/use-effect-once';
import useRoutes from 'hooks/use-routes';
import NotFound from 'pages/not-found';
import { RenderCurrentStaticPage } from 'pages/static/render-current-static-page';
import { useLocale } from 'state/intl';
import { PROD_DEFAULT_LANGUAGE, PROD_SUPPORTED_LANGUAGES } from 'state/intl/supported-languages';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { usePageManagerContext } from 'state/static-page-manager';
import { useGetStaticPageByRoute } from 'state/static-page-manager/hooks/use-get-static-page-by-route';
import { routes as appRoutes } from 'utils/routing';
import { isRedirectedToSimplexUrl } from 'utils/simplex';

import { getConfigValue } from '../../utils/environment';

const Container = styled.div`
  padding-bottom: ${p => (p.noBottomPadding ? '0' : '4.9rem')};

  ${Styles.mobile`
    margin-top: 0;
    padding-top: 0;
  `}
`;

const StaticPageContainer = () => {
  const { routesLoading, loadRoutes, loadRoutesHasBeenCalled } = usePageManagerContext();
  useEffectOnce(() => {
    // If the loadRoutes query has not been called yet, load the static routes list
    if (!loadRoutesHasBeenCalled) {
      loadRoutes();
    }
  });
  if (!loadRoutesHasBeenCalled || routesLoading) {
    return null;
  }
  return <StaticPage />;
};

export const StaticPage = () => {
  const { currentPage, retrieveStaticPageById, routes, routesLoading } = usePageManagerContext();
  const { getLocalizedRouteForPath } = useRoutes();
  const isNutritionExplorerEnabled = useFlag(LaunchDarklyFlag.ENABLE_NUTRITION_EXPLORER);
  const cleanId = useCallback(str => str?.match(/[\w-]+/g).join(''), []);
  const { language: currentLanguage, region, setCurrentLocale } = useLocale();
  const location = useLocation();
  // Remove first and last slash to check the "staticPath"
  const staticPath = (location.pathname || '').replace(/^\/|\/$/g, '');
  const navigate = useNavigate();
  const targetPage = useMemo(() => {
    let result;
    routes.forEach(route => {
      if (routesLoading) {
        result = undefined;
      }

      const staticLocalizedPageLanguage = Object.keys(PROD_SUPPORTED_LANGUAGES).find(
        lang => delv(route, `localePath.${lang}.current`) === staticPath
      );

      if (staticLocalizedPageLanguage) {
        result = { route, language: staticLocalizedPageLanguage };
      }

      if (delv(route, `path.current`) === staticPath) {
        result = { route, language: PROD_DEFAULT_LANGUAGE[region] };
      }
    });
    return result;
  }, [routes, routesLoading, staticPath, region]);

  useEffect(() => {
    if (!targetPage) {
      return;
    }
    if (targetPage.language !== currentLanguage) {
      setCurrentLocale(targetPage.language, region);
    }

    retrieveStaticPageById(
      targetPage.route._id,
      targetPage.route?.path?.current === appRoutes.nutritionExplorer.slice(1)
    );
  }, [targetPage, currentLanguage, retrieveStaticPageById, region, setCurrentLocale]);

  const is404 = useMemo(() => !routesLoading && !targetPage, [routesLoading, targetPage]);
  // Override page links so they are handled like a SPA.
  useEffect(() => {
    if (currentPage && currentPage.pageHtml) {
      const id = currentPage._id;
      document.querySelectorAll(`.page-${cleanId(id)} a`).forEach(el => {
        // Internal paths are prefixed with '!'
        const internalPath = el.pathname.match(/!([\w-]+)/);
        if (internalPath) {
          el.onclick = e => {
            e.preventDefault();
            navigate(`/${internalPath[1]}`);
          };
        }
      });
    }
  }, [cleanId, currentPage, navigate]);

  const showNutritionExplorer =
    isNutritionExplorerEnabled &&
    currentPage?.widgets?.some(widget => widget._type === 'nutritionExplorerWidget');

  if (is404) {
    // If it is a SEO simplex URL navigate home.
    const url = getConfigValue('urls').webBaseUrl || null;
    const wasRedirectedtoSimplex = isRedirectedToSimplexUrl(location.pathname, url);

    if (wasRedirectedtoSimplex) {
      return <Navigate to={appRoutes.base} />;
    }
    // if the route wasn't found see if we can translate it
    const fullPath = '/' + staticPath;
    const foundPath = getLocalizedRouteForPath(fullPath);
    // If found redirect
    if (foundPath !== fullPath) {
      return <Navigate to={foundPath} />;
    }
    return <NotFound />;
  }

  return currentPage ? (
    <div data-testid="static-page">
      <Helmet title={currentPage.title} />
      <BackArrowHistory />
      <Container
        noBottomPadding={showNutritionExplorer}
        className={`page-${cleanId(currentPage._id)} static-page`}
      >
        <RenderCurrentStaticPage currentPage={currentPage} />
      </Container>
    </div>
  ) : (
    <LoadingContainer>
      <LoadingAnimation fillColor={Styles.color.primary} />
    </LoadingContainer>
  );
};

/**
 * This function takes a pathname as param and render a Static Page component that can be embedded
 * inside a page / component so we're not limited to using routes for static content.
 */
export const StaticPageEmbedded = ({ pathname }) => {
  const { currentPage, getStaticPageByRouteId, is404 } = useGetStaticPageByRoute();

  useEffect(() => {
    getStaticPageByRouteId(pathname);
  }, [getStaticPageByRouteId, pathname]);

  if (is404) {
    return <NotFound />;
  }

  return (
    <div data-testid="static-page-embeded">
      {currentPage ? (
        <RenderCurrentStaticPage currentPage={currentPage} />
      ) : (
        <LoadingContainer>
          <LoadingAnimation fillColor={Styles.color.primary} />
        </LoadingContainer>
      )}
    </div>
  );
};

export default StaticPageContainer;
